import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Logo } from './Logo'
import { LanguageSwitcher } from './LanguageSwitcher'
import { openWhatsApp, whatsappEnabled } from '../lib/whatsapp'
import { WhatsAppIcon } from './WhatsAppIcon'
import { COUNTRIES, REGIONAL_PLANS_META } from '../data/catalog'
import { languageFromPath, withLang } from '../i18n/i18n'

type FooterItem = { label: string; href?: string; whatsapp?: boolean; prefill?: string }
interface FooterCol {
  h: string
  items: FooterItem[]
  more?: { label: string; href: string }
}

const FOOTER_COUNTRY_CAP = 6
const FOOTER_REGIONAL_CAP = 5

function FooterLink({ item }: { item: FooterItem }) {
  if (item.whatsapp) {
    return (
      <li>
        <button
          type="button"
          className="footer-wa-link"
          onClick={() => openWhatsApp(item.prefill ?? '', 'footer')}
        >
          <span className="footer-wa-dot"><WhatsAppIcon size={11} /></span>
          {item.label}
        </button>
      </li>
    )
  }
  if (item.href!.startsWith('/')) {
    return (
      <li>
        <Link to={item.href!}>{item.label}</Link>
      </li>
    )
  }
  return (
    <li>
      <a href={item.href!}>{item.label}</a>
    </li>
  )
}

export function Footer() {
  const { t } = useTranslation()
  const location = useLocation()
  const lang = languageFromPath(location.pathname)

  // Country / region names get translated through common.{countries,regions}.
  // Falls back to the catalog name if there's no key (e.g. a country
  // added before its translation was authored).
  const countryName = (code: string, fallback: string): string => {
    const k = `countries.${code}`
    const v = t(k)
    return v === k ? fallback : v
  }
  const regionName = (code: string, fallback: string): string => {
    const k = `regions.${code}`
    const v = t(k)
    return v === k ? fallback : v
  }

  const cols: FooterCol[] = [
    {
      h: t('footer.destinationsHeading'),
      items: COUNTRIES.filter((c) => c.popular)
        .slice(0, FOOTER_COUNTRY_CAP)
        .map((c) => ({
          label: countryName(c.code, c.name),
          href: withLang(`/destinations/${c.code.toLowerCase()}`, lang),
        })),
      more: { label: t('footer.allDestinations'), href: withLang('/destinations', lang) },
    },
    {
      h: t('footer.regionalHeading'),
      items: REGIONAL_PLANS_META.slice(0, FOOTER_REGIONAL_CAP).map((r) => ({
        label: regionName(r.code, r.name),
        href: withLang(`/destinations/${r.code.toLowerCase()}`, lang),
      })),
      more: { label: t('footer.allBundles'), href: withLang('/destinations', lang) },
    },
    {
      h: t('footer.helpHeading'),
      items: [
        { label: t('footer.links.howItWorks'), href: withLang('/how-it-works', lang) },
        { label: t('footer.links.compatibleDevices'), href: withLang('/compatible-devices', lang) },
        { label: t('footer.links.faq'), href: withLang('/faq', lang) },
        { label: t('footer.links.promise'), href: withLang('/refund-policy', lang) },
        { label: t('footer.links.about'), href: withLang('/about', lang) },
        { label: t('footer.links.privacy'), href: withLang('/privacy', lang) },
        { label: t('footer.links.blog'), href: 'https://blog.nimvoy.com' },
        // WhatsApp is deliberately NOT an href — the wa.me URL (which
        // contains the number) is assembled on click. See lib/whatsapp.ts.
        ...(whatsappEnabled()
          ? [{ label: t('whatsapp.footerLink'), whatsapp: true, prefill: t('whatsapp.prefill') } as FooterItem]
          : []),
        { label: t('footer.links.contact'), href: 'mailto:support@nimvoy.com' },
      ],
    },
  ]

  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-brand">
          <div style={{ marginBottom: 16 }}>
            <Logo />
          </div>
          <p className="muted footer-tagline">{t('footer.tagline')}</p>
          <p className="footer-contact">
            {t('footer.contact')}{' '}
            <a href="mailto:support@nimvoy.com">support@nimvoy.com</a>
          </p>
        </div>
        {cols.map((c) => (
          <div key={c.h}>
            <h4>{c.h}</h4>
            <ul>
              {c.items.map((it, i) => (
                <FooterLink key={i} item={it} />
              ))}
            </ul>
            {c.more && (
              <Link to={c.more.href} className="footer-more">
                {c.more.label} →
              </Link>
            )}
          </div>
        ))}
      </div>
      <div className="footer-meta">
        <span>{t('footer.copyright')}</span>
        <span className="footer-meta-trust">{t('footer.trust')}</span>
        <span className="footer-meta-locale">
          <LanguageSwitcher variant="footer" />
        </span>
      </div>
    </footer>
  )
}
